/**
 * React auth context and hooks for the RealPranks redesign.
 *
 * Wraps Firebase Auth in an idiomatic React provider/hook pair. All live data
 * (the auth state, the user's Firestore profile, settings, access, and
 * subscription summary) is exposed as a single `useAuth()` return value so UI
 * components (Lovable-generated or otherwise) can read a flat shape without
 * worrying about multiple snapshot listeners.
 *
 * Legacy Memberstack migration flow from the old Angular site is intentionally
 * NOT ported. The redesign is Firebase-only for authentication. Users who
 * still need to migrate should sign in on the legacy site (rosscreations.com)
 * at least once to complete the migration, then use the redesign normally.
 *
 * -------------------------------------------------------------------------
 * Quick reference for UI builders (Lovable prompts, etc.)
 * -------------------------------------------------------------------------
 * Read auth state:
 *   const { user, profile, settings, subscriptions, signedIn, loading } = useAuth();
 *
 * Sign in / sign up / sign out / reset password:
 *   const { signIn, signUp, signOut, sendPasswordReset } = useAuthActions();
 *
 * Fine-grained access checks:
 *   const { canAccessCreator } = useAuth();
 *
 * Each action returns a discriminated { success } | { error } result — never
 * throws — so forms can render errors inline without try/catch.
 * -------------------------------------------------------------------------
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  EmailAuthProvider,
  fetchSignInMethodsForEmail,
  getAdditionalUserInfo,
  GoogleAuthProvider,
  OAuthProvider,
  RecaptchaVerifier,
  createUserWithEmailAndPassword,
  linkWithPopup,
  linkWithCredential,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
  verifyBeforeUpdateEmail,
  type ActionCodeSettings,
  type ConfirmationResult,
  type RecaptchaParameters,
  type User,
  type UserCredential,
} from "firebase/auth";
import {
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  type Unsubscribe,
} from "firebase/firestore";

import {
  clearHomeSessionCache,
  writeHomeSessionCache,
} from "@/lib/home-session-cache";
import { currentSignupPath, currentSignupSource } from "@/lib/landing-source";
import { clearLibraryCaches } from "@/lib/library-cache";
import { debugLog } from "@/lib/debug-log";
import { getDb, getFirebaseAuth } from "./config";
import type {
  FirestoreUser,
  FirestoreUserMetaAccess,
  FirestoreUserMetaAdvancedSubscriptions,
  FirestoreUserMetaSettings,
  WithId,
} from "./types";

function getActionCodeSettings(): ActionCodeSettings {
  const origin =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://realpranks.com";
  return { url: `${origin}/auth/action`, handleCodeInApp: false };
}

function displayNameFromIdpProfile(
  profile: Record<string, unknown> | null | undefined,
): string | undefined {
  if (!profile) return undefined;
  const nameField = profile["name"];
  if (typeof nameField === "string" && nameField.trim()) return nameField.trim();
  if (nameField && typeof nameField === "object") {
    const n = nameField as Record<string, unknown>;
    const fn = n["firstName"] ?? n["first_name"];
    const ln = n["lastName"] ?? n["last_name"];
    const fnS = typeof fn === "string" ? fn.trim() : "";
    const lnS = typeof ln === "string" ? ln.trim() : "";
    if (fnS || lnS) return `${fnS} ${lnS}`.trim();
  }
  const given = profile["given_name"];
  const family = profile["family_name"];
  const g = typeof given === "string" ? given.trim() : "";
  const f = typeof family === "string" ? family.trim() : "";
  if (g || f) return `${g} ${f}`.trim();
  return undefined;
}

function inferOauthDisplayName(
  cred: UserCredential,
  profileExtras?: Partial<SignUpInput>,
): string | undefined {
  const fromForm = profileExtras?.name?.trim();
  if (fromForm) return fromForm;
  const idpProfile = getAdditionalUserInfo(cred)?.profile as
    | Record<string, unknown>
    | undefined;
  const fromIdp = displayNameFromIdpProfile(idpProfile);
  if (fromIdp) return fromIdp;
  return cred.user.displayName?.trim() || undefined;
}

// =====================================================================
// Public shape
// =====================================================================

export type AuthActionResult =
  | { success: true; message?: string }
  | {
      error: string;
      code?: string;
      mergeRequired?: {
        email: string;
        providerId: "google.com" | "apple.com";
        signInMethods: string[];
      };
    };

export type SignUpInput = {
  email?: string;
  password?: string;
  name?: string;
  phoneNumber?: string;
  instagram?: string;
  birthDay?: number;
  birthMonth?: number;
  birthYear?: number;
  gender?: "male" | "female" | "";
  tShirt?: string;
  canFlyOut?: boolean;
  avatarFile?: File;
};

export type SignInInput = {
  email: string;
  password: string;
};

export type LinkEmailInput = {
  email: string;
  password: string;
};

export type UpdateEmailInput = {
  email: string;
};

/** Result of starting a phone sign-in. Pass `confirm(code)` to verify. */
export type PhoneSignInChallenge = {
  confirm: (code: string) => Promise<AuthActionResult>;
};

export type AuthContextValue = {
  /** The raw Firebase `User`. `null` when signed out or still loading. */
  user: User | null;
  /**
   * True while the initial auth state check is in flight. Flip your UI
   * skeletons on this — not on `!user`, because `!user` includes "signed out"
   * which is a valid final state.
   */
  loading: boolean;
  /**
   * True when a real (non-anonymous) user is signed in. Matches the
   * `signedIn$` observable in the old Angular site.
   */
  signedIn: boolean;
  /**
   * True when the signed-in user has at least one active subscription.
   * This is a convenience for UIs that still drive "subscribed vs not"
   * badges from a single boolean. Prefer `creators` / `canAccessCreator`
   * for actual gating decisions.
   *
   * Note: this replaces the prototype `isSubscriber` boolean from the
   * original Lovable stub (which was a local toggle). It is now derived
   * from real subscription data and cannot be flipped by the UI.
   */
  isSubscriber: boolean;
  /**
   * @deprecated No longer functional. The original Lovable stub exposed a
   * local `toggle()` to flip `isSubscriber` for prototyping. Subscription
   * state now comes from Firestore. Replace dev toggles with a real
   * sign-in / subscribe flow. Calling this prints a one-time console warning.
   */
  toggle: () => void;

  /** The user's `users/{uid}` Firestore document. */
  profile: WithId<FirestoreUser> | null;
  /** The user's `users/{uid}/meta/settings` Firestore document. */
  settings: FirestoreUserMetaSettings | null;
  /** The user's `users/{uid}/meta/advanced-subscriptions` summary. */
  subscriptions: FirestoreUserMetaAdvancedSubscriptions | null;
  /** The user's `users/{uid}/meta/access` role-based access doc. */
  access: FirestoreUserMetaAccess | null;

  /**
   * Computed list of creator IDs the signed-in user may access, or the
   * literal string `"all"` if they have universal access. Derived from
   * `subscriptions`. Always `[]` while signed out.
   */
  creators: "all" | string[];

  /**
   * Returns true if the signed-in user has an active subscription granting
   * access to the creator with this ID (or "all"-tier access).
   */
  canAccessCreator: (creatorId: string) => boolean;

  /**
   * Which generation of the membership product this user belongs to.
   *  - `'legacy'`: they were subscribed on the original Angular site (the
   *    seed migration stamps this; legacy members get unlimited vault access).
   *  - `'v2'`: they subscribed via the realpranks-redesign and follow the new
   *    weekly-release entitlement model.
   *  - `undefined`: the field hasn't been populated yet (e.g. never-subscribed
   *    user, or the seed migration hasn't run against this account).
   *
   * Read-only mirror of `users/{uid}.membershipType`. Cannot be written from
   * the client (the user-doc rule whitelist excludes this field).
   */
  membershipType: "legacy" | "v2" | undefined;
  /**
   * Convenience boolean: `membershipType === 'legacy'`. Use this to gate the
   * "show every video in the vault" behavior. Always `false` while loading or
   * signed out.
   */
  isLegacyMember: boolean;
  /** Provider IDs currently linked to this account (e.g. `password`, `google.com`). */
  signInMethods: string[];
  /** Human-friendly primary sign-in method label shown in account UI. */
  primarySignInMethodLabel: string;
  /** True when the account can authenticate with a password. */
  hasPasswordAuth: boolean;
  /** Deferred account merge request captured from sign-in conflicts. */
  pendingAccountMerge: PendingAccountMerge | null;

  // ---- Actions ----
  signIn: (input: SignInInput) => Promise<AuthActionResult>;
  signUp: (input: SignUpInput) => Promise<AuthActionResult>;
  signOut: () => Promise<AuthActionResult>;
  sendPasswordReset: (email: string) => Promise<AuthActionResult>;
  sendCurrentUserEmailVerification: () => Promise<AuthActionResult>;
  linkEmailPassword: (input: LinkEmailInput) => Promise<AuthActionResult>;
  requestEmailChange: (input: UpdateEmailInput) => Promise<AuthActionResult>;
  /** Sign in with Google via popup. Seeds profile docs if first time. */
  signInWithGoogle: (
    profileExtras?: Partial<SignUpInput>,
  ) => Promise<AuthActionResult>;
  /** Sign in with Apple via popup. Seeds profile docs if first time. */
  signInWithApple: (
    profileExtras?: Partial<SignUpInput>,
  ) => Promise<AuthActionResult>;
  /**
   * Start a phone sign-in. The `recaptchaContainerId` must point to an empty
   * `<div>` already mounted in the DOM. Resolves to a challenge object whose
   * `confirm(code)` finishes the sign-in.
   */
  startPhoneSignIn: (
    phoneNumber: string,
    recaptchaContainerId: string,
    profileExtras?: Partial<SignUpInput>,
  ) => Promise<{ success: true; challenge: PhoneSignInChallenge } | { error: string }>;
  deferAccountMerge: (request: PendingAccountMerge) => void;
  clearAccountMerge: () => void;
  completeDeferredMerge: () => Promise<AuthActionResult>;
};

export type PendingAccountMerge = {
  email: string;
  providerId: "google.com" | "apple.com";
  signInMethods: string[];
  createdAt: number;
};

const AuthContext = createContext<AuthContextValue | null>(null);

// =====================================================================
// reCAPTCHA (phone auth)
// =====================================================================

/**
 * Options for {@link RecaptchaVerifier} with invisible reCAPTCHA, matching the
 * Firebase snippets-web shape:
 * {@link https://github.com/firebase/snippets-web/blob/95c8c159ff4d90af442352f058406f1aeb8adcbb/snippets/auth-next/phone-auth/auth_phone_recaptcha_verifier_invisible.js}
 */
function invisiblePhoneRecaptchaParams(handlers: {
  onSolved: (response: string) => void;
  onExpired: () => void;
  onError: () => void;
}): RecaptchaParameters {
  return {
    size: "invisible",
    callback: (response: string) => {
      handlers.onSolved(response);
    },
    "expired-callback": () => {
      handlers.onExpired();
    },
    "error-callback": () => {
      handlers.onError();
    },
  };
}

// =====================================================================
// Provider
// =====================================================================

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const [profile, setProfile] = useState<WithId<FirestoreUser> | null>(null);
  const [settings, setSettings] = useState<FirestoreUserMetaSettings | null>(
    null,
  );
  const [subscriptions, setSubscriptions] =
    useState<FirestoreUserMetaAdvancedSubscriptions | null>(null);
  const [access, setAccess] = useState<FirestoreUserMetaAccess | null>(null);
  const [pendingAccountMerge, setPendingAccountMerge] =
    useState<PendingAccountMerge | null>(null);

  // Cleanup registry for per-user Firestore listeners.
  const listenersRef = useRef<Unsubscribe[]>([]);
  const phoneRecaptchaRef = useRef<{
    containerId: string;
    verifier: RecaptchaVerifier;
  } | null>(null);

  const clearPhoneRecaptchaVerifier = useCallback(() => {
    try {
      phoneRecaptchaRef.current?.verifier.clear();
    } catch {
      /* noop */
    }
    phoneRecaptchaRef.current = null;
  }, []);

  // --- Auth state listener (mount once)
  useEffect(() => {
    const auth = getFirebaseAuth();
    const unsub = onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // --- Profile + settings + subscriptions + access listeners (re-bind on user change)
  useEffect(() => {
    // Tear down previous listeners before rebinding.
    listenersRef.current.forEach((u) => u());
    listenersRef.current = [];

    if (!user) {
      setProfile(null);
      setSettings(null);
      setSubscriptions(null);
      setAccess(null);
      return;
    }

    const db = getDb();
    const uid = user.uid;

    const profileUnsub = onSnapshot(
      doc(db, "users", uid),
      (snap) => {
        if (!snap.exists()) {
          setProfile(null);
          return;
        }
        setProfile({ id: snap.id, ...(snap.data() as FirestoreUser) });
      },
      (err) => console.error("[auth] profile listener error", err),
    );

    const settingsUnsub = onSnapshot(
      doc(db, "users", uid, "meta", "settings"),
      (snap) =>
        setSettings(
          snap.exists() ? (snap.data() as FirestoreUserMetaSettings) : null,
        ),
      (err) => console.error("[auth] settings listener error", err),
    );

    const subsUnsub = onSnapshot(
      doc(db, "users", uid, "meta", "advanced-subscriptions"),
      (snap) =>
        setSubscriptions(
          snap.exists()
            ? (snap.data() as FirestoreUserMetaAdvancedSubscriptions)
            : null,
        ),
      (err) => console.error("[auth] subscriptions listener error", err),
    );

    const accessUnsub = onSnapshot(
      doc(db, "users", uid, "meta", "access"),
      (snap) =>
        setAccess(
          snap.exists() ? (snap.data() as FirestoreUserMetaAccess) : null,
        ),
      (err) => {
        // This doc is admin-writable only; a PERMISSION_DENIED is normal for
        // users without a populated access record, so we swallow quietly.
        if ((err as { code?: string }).code !== "permission-denied") {
          console.error("[auth] access listener error", err);
        }
        setAccess(null);
      },
    );

    listenersRef.current = [
      profileUnsub,
      settingsUnsub,
      subsUnsub,
      accessUnsub,
    ];

    // Best-effort lastSignedInAt bookkeeping, matching the old site.
    setDoc(
      doc(db, "users", uid),
      { lastSignedInAt: serverTimestamp() },
      { merge: true },
    ).catch((err) => {
      console.warn("[auth] failed to update lastSignedInAt", err);
    });

    return () => {
      listenersRef.current.forEach((u) => u());
      listenersRef.current = [];
    };
  }, [user]);

  const signedIn = !!user && !user.isAnonymous;
  const signInMethods = useMemo(() => {
    if (!user?.providerData?.length) return [];
    return Array.from(
      new Set(
        user.providerData
          .map((provider) => provider.providerId)
          .filter((providerId): providerId is string => !!providerId),
      ),
    );
  }, [user]);
  const hasPasswordAuth = signInMethods.includes("password");
  const primarySignInMethodLabel = useMemo(
    () => getPrimarySignInMethodLabel(signInMethods),
    [signInMethods],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(PENDING_ACCOUNT_MERGE_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as PendingAccountMerge;
      if (isValidPendingAccountMerge(parsed)) {
        setPendingAccountMerge(parsed);
      } else {
        window.localStorage.removeItem(PENDING_ACCOUNT_MERGE_STORAGE_KEY);
      }
    } catch {
      window.localStorage.removeItem(PENDING_ACCOUNT_MERGE_STORAGE_KEY);
    }
  }, []);

  const deferAccountMerge = useCallback((request: PendingAccountMerge) => {
    setPendingAccountMerge(request);
    if (typeof window === "undefined") return;
    window.localStorage.setItem(PENDING_ACCOUNT_MERGE_STORAGE_KEY, JSON.stringify(request));
  }, []);

  const clearAccountMerge = useCallback(() => {
    setPendingAccountMerge(null);
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(PENDING_ACCOUNT_MERGE_STORAGE_KEY);
  }, []);

  const toggleWarnedRef = useRef(false);
  const toggle = useCallback(() => {
    if (!toggleWarnedRef.current) {
      toggleWarnedRef.current = true;
      // eslint-disable-next-line no-console
      console.warn(
        "[auth] useAuth().toggle() is a no-op. Subscription state is now driven by real Firestore data. Replace any prototype toggles with a real sign-in / subscribe flow.",
      );
    }
  }, []);

  const creators = useMemo<"all" | string[]>(() => {
    if (!subscriptions?.subscriptions) return [];
    const aggregate: Record<string, true> | "all" = subscriptions.subscriptions
      .filter((s) => s.status === "active")
      .reduce<Record<string, true> | "all">((acc, cur) => {
        if (acc === "all") return "all";
        if (cur.creators === "all") return "all";
        (cur.creators as string[]).forEach((id) => {
          acc[id] = true;
        });
        return acc;
      }, {});
    return aggregate === "all" ? "all" : Object.keys(aggregate);
  }, [subscriptions]);

  const canAccessCreator = useCallback(
    (creatorId: string) => {
      if (creators === "all") return true;
      return creators.includes(creatorId);
    },
    [creators],
  );

  const isSubscriber =
    signedIn && (creators === "all" || creators.length > 0);

  // Persist homepage routing hint for instant return visits.
  useEffect(() => {
    if (loading) return;
    if (!signedIn) {
      clearHomeSessionCache();
      return;
    }
    writeHomeSessionCache({ signedIn: true, isSubscriber });
  }, [loading, signedIn, isSubscriber]);

  const membershipType = profile?.membershipType;
  const isLegacyMember = signedIn && membershipType === "legacy";

  useEffect(() => {
    if (loading) return;
    debugLog("auth", "subscription snapshot", {
      signedIn,
      isSubscriber,
      membershipType,
      activeSubscriptionCount:
        subscriptions?.subscriptions?.filter((s) => s.status === "active").length ?? 0,
      subscriptionStatuses:
        subscriptions?.subscriptions?.map((s) => s.status) ?? [],
    });
  }, [loading, signedIn, isSubscriber, membershipType, subscriptions]);

  const signIn = useCallback(
    async (input: SignInInput): Promise<AuthActionResult> => {
      if (!input.email?.includes("@")) {
        return { error: "Email must be a valid email." };
      }
      if ((input.password?.length ?? 0) < 8) {
        return { error: "Password must be 8 characters or longer." };
      }
      try {
        await signInWithEmailAndPassword(
          getFirebaseAuth(),
          input.email,
          input.password,
        );
        return { success: true, message: "Signed in successfully." };
      } catch (err) {
        return { error: firebaseErrorToMessage(err) };
      }
    },
    [],
  );

  /**
   * Seeds (merges) the user's Firestore docs after a successful auth event.
   * Safe to call multiple times — uses `setDoc(..., { merge: true })`.
   * If `avatarFile` is provided, uploads it via the standard avatar helper
   * (lazy-imported to avoid a circular dep with `./users`).
   */
  const seedProfileDocs = useCallback(
    async (
      uid: string,
      input: Partial<SignUpInput>,
      opts: {
        isNew: boolean;
        emailFallback?: string | null;
        avatarUrlFallback?: string | null;
      } = { isNew: true },
    ): Promise<AuthActionResult> => {
      const db = getDb();
      try {
        const writes: Promise<unknown>[] = [];
        const userPatch: Partial<FirestoreUser> = {
          lastSignedInAt: serverTimestamp() as unknown as FirestoreUser["lastSignedInAt"],
        };
        if (opts.isNew) {
          userPatch.createdAt =
            serverTimestamp() as unknown as FirestoreUser["createdAt"];
        }
        if (input.name !== undefined) userPatch.name = input.name;
        if (opts.isNew && opts.avatarUrlFallback) {
          userPatch.avatar = opts.avatarUrlFallback;
        }
        writes.push(setDoc(doc(db, "users", uid), userPatch, { merge: true }));

        const settingsPatch: Partial<FirestoreUserMetaSettings> = {};
        if (input.phoneNumber !== undefined)
          settingsPatch.phoneNumber = input.phoneNumber;
        if (input.instagram !== undefined)
          settingsPatch.instagram = input.instagram;
        if (
          input.birthDay !== undefined ||
          input.birthMonth !== undefined ||
          input.birthYear !== undefined
        ) {
          settingsPatch.birthDate = {
            day: input.birthDay,
            month: input.birthMonth,
            year: input.birthYear,
          };
        }
        if (input.gender !== undefined) settingsPatch.gender = input.gender;
        if (input.tShirt !== undefined) settingsPatch.tShirt = input.tShirt;
        const resolvedEmail = input.email ?? opts.emailFallback ?? undefined;
        if (resolvedEmail) settingsPatch.email = resolvedEmail;
        if (input.canFlyOut !== undefined)
          settingsPatch.canFlyOut = input.canFlyOut;
        if (opts.isNew) {
          settingsPatch.notifications = { email: true };
          settingsPatch.blockedUsers = [];
          settingsPatch.signupSource = currentSignupSource();
          settingsPatch.signupPath = currentSignupPath();
        }
        if (Object.keys(settingsPatch).length > 0) {
          writes.push(
            setDoc(
              doc(db, "users", uid, "meta", "settings"),
              settingsPatch,
              { merge: true },
            ),
          );
        }
        await Promise.all(writes);

        if (input.avatarFile) {
          // Lazy import to avoid pulling Storage SDK on every page that uses auth.
          const { uploadMyAvatar } = await import("./users");
          const result = await uploadMyAvatar(input.avatarFile);
          if ("error" in result) return result;
        }
        return { success: true };
      } catch (err) {
        return {
          error:
            "Signed in, but we could not save your profile. Please update it from your account page.",
        };
      }
    },
    [],
  );

  const signUp = useCallback(
    async (input: SignUpInput): Promise<AuthActionResult> => {
      if (!input.email?.includes("@")) {
        return { error: "Email must be a valid email." };
      }
      if (!input.password || input.password.length < 8) {
        return { error: "Password must be 8 characters or longer." };
      }

      const auth = getFirebaseAuth();
      let uid: string;

      try {
        // If an anonymous user is currently signed in, link them instead of
        // creating a brand-new account so carts, drafts, etc. survive.
        if (auth.currentUser && auth.currentUser.isAnonymous) {
          const cred = EmailAuthProvider.credential(input.email, input.password);
          const linked = await linkWithCredential(auth.currentUser, cred);
          uid = linked.user.uid;
        } else {
          const created = await createUserWithEmailAndPassword(
            auth,
            input.email,
            input.password,
          );
          uid = created.user.uid;
        }
      } catch (err) {
        return { error: firebaseErrorToMessage(err) };
      }

      // Send the verification link (lands on /auth/action?mode=verifyEmail).
      // Best-effort: the account exists either way, and the verify-email page
      // offers a resend if this send fails.
      try {
        const currentUser = auth.currentUser;
        if (currentUser) {
          await sendEmailVerification(currentUser, getActionCodeSettings());
        }
      } catch (err) {
        logFirebaseErrorDebug("signup-send-verification-email", err, {
          email: input.email,
        });
      }

      const seed = await seedProfileDocs(uid, input, {
        isNew: true,
        emailFallback: input.email,
      });
      if ("error" in seed) return seed;
      return { success: true, message: "Account created." };
    },
    [seedProfileDocs],
  );

  const signInWithProvider = useCallback(
    async (
      provider: GoogleAuthProvider | OAuthProvider,
      profileExtras?: Partial<SignUpInput>,
    ): Promise<AuthActionResult> => {
      const auth = getFirebaseAuth();
      let cred: UserCredential;
      try {
        cred = await signInWithPopup(auth, provider);
      } catch (err) {
        const mergeRequired = await buildMergeRequiredError(
          err,
          provider.providerId as "google.com" | "apple.com",
        );
        if (mergeRequired) {
          const request: PendingAccountMerge = {
            email: mergeRequired.email,
            providerId: mergeRequired.providerId,
            signInMethods: mergeRequired.signInMethods,
            createdAt: Date.now(),
          };
          deferAccountMerge(request);
          return {
            error:
              "An account already exists with this email. Sign in using your current method, then merge this social sign-in.",
            code: "auth/account-exists-with-different-credential",
            mergeRequired,
          };
        }
        if (isPopupFallbackError(err)) {
          try {
            await signInWithRedirect(auth, provider);
            return {
              success: true,
              message: "Continuing sign-in in a new tab/window.",
            };
          } catch (redirectErr) {
            return { error: firebaseErrorToMessage(redirectErr) };
          }
        }
        return { error: firebaseErrorToMessage(err) };
      }
      const isNew =
        // Firebase exposes `isNewUser` via getAdditionalUserInfo, but the
        // shape works fine via metadata: creation === lastSignIn for first signin.
        cred.user.metadata.creationTime ===
        cred.user.metadata.lastSignInTime;
      const inferredName = inferOauthDisplayName(cred, profileExtras);
      const seedInput: Partial<SignUpInput> = { ...profileExtras };
      if (inferredName) seedInput.name = inferredName;
      const seed = await seedProfileDocs(cred.user.uid, seedInput, {
        isNew,
        emailFallback: cred.user.email ?? null,
        avatarUrlFallback: cred.user.photoURL ?? null,
      });
      if ("error" in seed) return seed;
      return { success: true, message: "Signed in." };
    },
    [deferAccountMerge, seedProfileDocs],
  );

  const signInWithGoogle = useCallback(
    (profileExtras?: Partial<SignUpInput>) => {
      const provider = new GoogleAuthProvider();
      provider.addScope("email");
      provider.addScope("profile");
      provider.setCustomParameters({ prompt: "select_account" });
      return signInWithProvider(provider, profileExtras);
    },
    [signInWithProvider],
  );

  const signInWithApple = useCallback(
    (profileExtras?: Partial<SignUpInput>) => {
      const provider = new OAuthProvider("apple.com");
      provider.addScope("email");
      provider.addScope("name");
      return signInWithProvider(provider, profileExtras);
    },
    [signInWithProvider],
  );

  const startPhoneSignIn = useCallback(
    async (
      phoneNumber: string,
      recaptchaContainerId: string,
      profileExtras?: Partial<SignUpInput>,
    ): Promise<
      { success: true; challenge: PhoneSignInChallenge } | { error: string }
    > => {
      const auth = getFirebaseAuth();
      let verifier: RecaptchaVerifier;
      try {
        const existing = phoneRecaptchaRef.current;
        if (existing && existing.containerId === recaptchaContainerId) {
          verifier = existing.verifier;
        } else {
          existing?.verifier.clear();
          verifier = new RecaptchaVerifier(
            auth,
            recaptchaContainerId,
            invisiblePhoneRecaptchaParams({
              onSolved: (_response) => {
                // Snippet: reCAPTCHA solved — SMS send still runs via
                // `signInWithPhoneNumber` below, which consumes the verifier token.
                void _response;
              },
              onExpired: () => {
                clearPhoneRecaptchaVerifier();
              },
              onError: () => {
                clearPhoneRecaptchaVerifier();
              },
            }),
          );
          phoneRecaptchaRef.current = {
            containerId: recaptchaContainerId,
            verifier,
          };
        }
      } catch (err) {
        const origin = clientAuthErrorContext();
        logFirebaseErrorDebug("phone-recaptcha-init", err, {
          recaptchaContainerId,
          ...origin,
        });
        return { error: firebaseErrorToMessage(err, origin) };
      }

      let confirmation: ConfirmationResult;
      try {
        confirmation = await signInWithPhoneNumber(
          auth,
          phoneNumber,
          verifier,
        );
      } catch (err) {
        const code = (err as { code?: string }).code;
        const origin = clientAuthErrorContext();
        const hostLower = origin?.hostname.toLowerCase();
        logFirebaseErrorDebug("phone-send-code", err, {
          phoneNumber,
          recaptchaContainerId,
          ...origin,
          hint:
            hostLower === "localhost" || hostLower === "[::1]"
              ? "Real SMS is often rejected on http://localhost; use http://127.0.0.1, a test number, Auth emulator, or HTTPS deploy. See Firebase JS SDK issue #8387."
              : undefined,
        });

        // A stale/broken invisible reCAPTCHA token can wedge the verifier.
        // Test phone numbers can still succeed while real numbers fail in this state.
        if (code === "auth/invalid-app-credential" || code === "auth/captcha-check-failed") {
          clearPhoneRecaptchaVerifier();
        }

        return { error: firebaseErrorToMessage(err, origin) };
      }

      const challenge: PhoneSignInChallenge = {
        confirm: async (code: string) => {
          let cred: UserCredential;
          try {
            cred = await confirmation.confirm(code);
          } catch (err) {
            const origin = clientAuthErrorContext();
            logFirebaseErrorDebug("phone-confirm-code", err, {
              phoneNumber,
              ...origin,
            });
            return { error: firebaseErrorToMessage(err, origin) };
          }
          const isNew =
            cred.user.metadata.creationTime ===
            cred.user.metadata.lastSignInTime;
          const seed = await seedProfileDocs(
            cred.user.uid,
            {
              ...profileExtras,
              phoneNumber: profileExtras?.phoneNumber ?? phoneNumber,
            },
            { isNew, emailFallback: cred.user.email ?? null },
          );
          if ("error" in seed) return seed;
          return { success: true, message: "Signed in." };
        },
      };
      return { success: true as const, challenge };
    },
    [clearPhoneRecaptchaVerifier, seedProfileDocs],
  );

  const signOut = useCallback(async (): Promise<AuthActionResult> => {
    try {
      const uid = getFirebaseAuth().currentUser?.uid;
      clearHomeSessionCache();
      clearLibraryCaches(uid);
      await firebaseSignOut(getFirebaseAuth());
      return { success: true };
    } catch (err) {
      return { error: firebaseErrorToMessage(err) };
    }
  }, []);

  const sendPasswordReset = useCallback(
    async (email: string): Promise<AuthActionResult> => {
      if (!email?.includes("@")) {
        return { error: "Email must be a valid email." };
      }
      try {
        await firebaseSendPasswordResetEmail(getFirebaseAuth(), email, getActionCodeSettings());
        return {
          success: true,
          message: "If an account exists for that email, a reset link is on the way.",
        };
      } catch (err) {
        return { error: firebaseErrorToMessage(err) };
      }
    },
    [],
  );

  const sendCurrentUserEmailVerification = useCallback(
    async (): Promise<AuthActionResult> => {
      const currentUser = getFirebaseAuth().currentUser;
      if (!currentUser?.email) {
        return { error: "No email address is linked to this account yet." };
      }

      try {
        await sendEmailVerification(currentUser, getActionCodeSettings());
        return {
          success: true,
          message: `Verification sent to ${currentUser.email}.`,
        };
      } catch (err) {
        return { error: firebaseErrorToMessage(err) };
      }
    },
    [],
  );

  const linkEmailPassword = useCallback(
    async (input: LinkEmailInput): Promise<AuthActionResult> => {
      const email = input.email.trim().toLowerCase();
      if (!email.includes("@")) {
        return { error: "Please enter a valid email." };
      }
      if ((input.password?.length ?? 0) < 8) {
        return { error: "Password must be at least 8 characters." };
      }

      const auth = getFirebaseAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return { error: "Please sign in before linking an email address." };
      }

      if (
        currentUser.providerData.some(
          (provider) => provider.providerId === "password",
        )
      ) {
        return {
          error: "This account already has an email/password sign-in method.",
        };
      }

      if (currentUser.email && currentUser.email !== email) {
        return {
          error:
            "This account already has a different email. Email change is not supported in this flow yet.",
        };
      }

      try {
        const credential = EmailAuthProvider.credential(email, input.password);
        const linked = await linkWithCredential(currentUser, credential);
        await seedProfileDocs(
          linked.user.uid,
          { email },
          { isNew: false, emailFallback: email },
        );
        await sendEmailVerification(linked.user, getActionCodeSettings());
        return {
          success: true,
          message: `Email linked. Verification sent to ${email}.`,
        };
      } catch (err) {
        return { error: firebaseErrorToMessage(err) };
      }
    },
    [seedProfileDocs],
  );

  const requestEmailChange = useCallback(
    async (input: UpdateEmailInput): Promise<AuthActionResult> => {
      const newEmail = input.email.trim().toLowerCase();
      if (!newEmail.includes("@")) {
        return { error: "Please enter a valid email." };
      }

      const auth = getFirebaseAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return { error: "Please sign in before changing your email." };
      }
      if (!currentUser.email) {
        return {
          error:
            "No current email is linked. Add an email/password sign-in method first.",
        };
      }
      if (currentUser.email.toLowerCase() === newEmail) {
        return { error: "That is already your current email." };
      }

      try {
        await verifyBeforeUpdateEmail(currentUser, newEmail, getActionCodeSettings());
        return {
          success: true,
          message: `Confirmation sent to ${newEmail}. Your email updates after you confirm the link.`,
        };
      } catch (err) {
        return { error: firebaseErrorToMessage(err) };
      }
    },
    [],
  );

  const completeDeferredMerge = useCallback(async (): Promise<AuthActionResult> => {
    const auth = getFirebaseAuth();
    const currentUser = auth.currentUser;
    const request = pendingAccountMerge;
    if (!currentUser) {
      return { error: "Please sign in to merge accounts." };
    }
    if (!request) {
      return { error: "No pending account merge request found." };
    }

    const provider =
      request.providerId === "google.com"
        ? new GoogleAuthProvider()
        : new OAuthProvider("apple.com");
    if (request.providerId === "google.com") {
      provider.setCustomParameters({ prompt: "select_account" });
    }

    try {
      const linked = await linkWithPopup(currentUser, provider);
      clearAccountMerge();
      const inferredName = inferOauthDisplayName(linked);
      if (inferredName) {
        await seedProfileDocs(
          linked.user.uid,
          { name: inferredName },
          { isNew: false, emailFallback: linked.user.email ?? null },
        );
      }
      return { success: true, message: "Accounts merged successfully." };
    } catch (err) {
      return { error: firebaseErrorToMessage(err) };
    }
  }, [clearAccountMerge, pendingAccountMerge, seedProfileDocs]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      signedIn,
      isSubscriber,
      toggle,
      profile,
      settings,
      subscriptions,
      access,
      creators,
      canAccessCreator,
      membershipType,
      isLegacyMember,
      signInMethods,
      primarySignInMethodLabel,
      hasPasswordAuth,
      pendingAccountMerge,
      signIn,
      signUp,
      signOut,
      sendPasswordReset,
      sendCurrentUserEmailVerification,
      linkEmailPassword,
      requestEmailChange,
      signInWithGoogle,
      signInWithApple,
      startPhoneSignIn,
      deferAccountMerge,
      clearAccountMerge,
      completeDeferredMerge,
    }),
    [
      user,
      loading,
      signedIn,
      isSubscriber,
      toggle,
      profile,
      settings,
      subscriptions,
      access,
      creators,
      canAccessCreator,
      membershipType,
      isLegacyMember,
      signInMethods,
      primarySignInMethodLabel,
      hasPasswordAuth,
      pendingAccountMerge,
      signIn,
      signUp,
      signOut,
      sendPasswordReset,
      sendCurrentUserEmailVerification,
      linkEmailPassword,
      requestEmailChange,
      signInWithGoogle,
      signInWithApple,
      startPhoneSignIn,
      deferAccountMerge,
      clearAccountMerge,
      completeDeferredMerge,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// =====================================================================
// Hooks
// =====================================================================

/** Full auth context value. Most UI code should use this. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** Narrower hook returning only the action callbacks (no re-renders when state changes). */
export function useAuthActions() {
  const {
    signIn,
    signUp,
    signOut,
    sendPasswordReset,
      sendCurrentUserEmailVerification,
      linkEmailPassword,
    requestEmailChange,
    completeDeferredMerge,
    clearAccountMerge,
  } = useAuth();
  return {
    signIn,
    signUp,
    signOut,
    sendPasswordReset,
    sendCurrentUserEmailVerification,
    linkEmailPassword,
    requestEmailChange,
    completeDeferredMerge,
    clearAccountMerge,
  };
}

// =====================================================================
// Internals
// =====================================================================

/** Origin context for mapping auth errors (e.g. localhost vs real host for phone SMS). */
function clientAuthErrorContext():
  | { hostname: string; origin: string }
  | undefined {
  if (typeof window === "undefined") return undefined;
  return {
    hostname: window.location.hostname,
    origin: window.location.origin,
  };
}

/** Firebase Auth error codes mapped to user-facing messages. */
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  "auth/invalid-email": "Please enter a valid email.",
  "auth/user-disabled": "This account has been disabled.",
  "auth/user-not-found": "No account exists for that email.",
  "auth/wrong-password": "Incorrect email or password.",
  "auth/invalid-credential": "Incorrect email or password.",
  "auth/invalid-login-credentials": "Incorrect email or password.",
  "auth/email-already-in-use":
    "An account with that email already exists. Try signing in instead.",
  "auth/weak-password": "Password must be at least 8 characters.",
  "auth/too-many-requests":
    "Too many phone sign-in attempts from this network. Wait about an hour, then try again. A hard refresh will not clear this limit.",
  "auth/network-request-failed":
    "Network error. Check your connection and try again.",
  "auth/operation-not-allowed":
    "This sign-in method is not enabled in Firebase Authentication.",
  "auth/invalid-phone-number":
    "Phone number format is invalid. Use international format, e.g. +15551234567.",
  "auth/missing-phone-number":
    "Please enter a phone number before requesting a code.",
  "auth/invalid-app-credential":
    "Phone verification failed app checks (reCAPTCHA, API key referrer restrictions, or App Check). Confirm Firebase Auth settings and Google Cloud credentials for this origin.",
  "auth/captcha-check-failed":
    "reCAPTCHA verification failed. Refresh the page and try again.",
  "auth/quota-exceeded":
    "SMS quota exceeded for this Firebase project. Try again later.",
  "auth/account-exists-with-different-credential":
    "An account with this email exists using a different sign-in method. Use that method first, then link Google from account settings.",
  "auth/popup-closed-by-user": "Sign-in window was closed. Please try again.",
  "auth/popup-blocked":
    "Your browser blocked the sign-in popup. Please allow popups and try again.",
  "auth/requires-recent-login":
    "Please sign in again to finish this action.",
};

const PENDING_ACCOUNT_MERGE_STORAGE_KEY =
  "realpranks.pending-account-merge.v1";

function isValidPendingAccountMerge(
  value: PendingAccountMerge | null | undefined,
): value is PendingAccountMerge {
  return !!value?.email && !!value?.providerId && Array.isArray(value.signInMethods);
}

function getPrimarySignInMethodLabel(signInMethods: string[]): string {
  if (signInMethods.includes("google.com")) return "Google";
  if (signInMethods.includes("apple.com")) return "Apple";
  if (signInMethods.includes("phone")) return "Phone";
  if (signInMethods.includes("password")) return "Email & Password";
  return "Unknown";
}

async function buildMergeRequiredError(
  err: unknown,
  attemptedProviderId: "google.com" | "apple.com",
): Promise<{
  email: string;
  providerId: "google.com" | "apple.com";
  signInMethods: string[];
} | null> {
  const anyErr = err as {
    code?: string;
    customData?: { email?: string };
  };
  if (anyErr.code !== "auth/account-exists-with-different-credential") return null;

  const email = anyErr.customData?.email;
  if (!email) return null;

  try {
    const methods = await fetchSignInMethodsForEmail(getFirebaseAuth(), email);
    return {
      email,
      providerId: attemptedProviderId,
      signInMethods: methods,
    };
  } catch {
    return {
      email,
      providerId: attemptedProviderId,
      signInMethods: [],
    };
  }
}

function isPopupFallbackError(err: unknown): boolean {
  const code = (err as { code?: string } | null)?.code;
  return (
    code === "auth/popup-blocked" ||
    code === "auth/operation-not-supported-in-this-environment"
  );
}

function logFirebaseErrorDebug(
  flow: string,
  err: unknown,
  context?: Record<string, unknown>,
) {
  const anyErr = err as {
    code?: string;
    message?: string;
    customData?: unknown;
    name?: string;
  };
  console.error("[firebase:error]", {
    flow,
    code: anyErr?.code,
    message: anyErr?.message,
    name: anyErr?.name,
    customData: anyErr?.customData,
    context: context ?? {},
  });
}

export function firebaseErrorToMessage(
  err: unknown,
  context?: { hostname?: string; origin?: string },
): string {
  if (!err) return "Something went wrong. Please try again.";
  const anyErr = err as { code?: string; message?: string };
  const hostLower = context?.hostname?.toLowerCase();

  if (anyErr.code === "auth/invalid-app-credential") {
    if (hostLower === "localhost" || hostLower === "[::1]") {
      return (
        "Firebase usually rejects real SMS verification when the app is opened as http://localhost (same error as a bad reCAPTCHA token). " +
        "Open the app as http://127.0.0.1 with the same port, use a Firebase Authentication test phone number, the Auth emulator, or your deployed HTTPS site."
      );
    }
  }

  if (anyErr.code && AUTH_ERROR_MESSAGES[anyErr.code]) {
    return AUTH_ERROR_MESSAGES[anyErr.code];
  }
  if (anyErr.message) return anyErr.message;
  return "Something went wrong. Please try again.";
}
